import type {
  TaskContext,
  BudgetState,
  CalibrationHistory,
  ModelInfo,
  Recommendation,
  RouterConfig,
} from "./types.js";
import { getDefaultModels } from "./models.js";
import { scoreModelForTask, estimateCost, estimateTokensForTask } from "./scorer.js";
import { checkBudget } from "./budget.js";

export interface Router {
  recommend(task: TaskContext, budgetState?: BudgetState): Recommendation;
  recommendBatch(
    tasks: TaskContext[],
    budgetState?: BudgetState,
  ): Recommendation[];
  getDefaultModel(tier: "frontier" | "mid" | "economy"): ModelInfo | undefined;
}

/**
 * Create a router instance configured with intent, calibration history,
 * and an optional custom model registry.
 */
export function createRouter(config: RouterConfig = {}): Router {
  const models = config.models ?? getDefaultModels();
  const calibration = config.calibration;
  const preferredProvider = config.intent?.providers?.preferred;
  const guardrailTemplates =
    config.intent?.guardrails?.security?.templates ?? [];
  const qualityGuardrails = config.intent?.guardrails?.quality ?? [];

  function recommend(
    task: TaskContext,
    budgetState?: BudgetState,
  ): Recommendation {
    // Score every model
    const scored = models.map((model) => {
      const result = scoreModelForTask(model, task, calibration);

      // Apply provider preference boost
      let adjustedScore = result.score;
      if (preferredProvider && model.provider === preferredProvider) {
        adjustedScore += 0.15;
      }

      return {
        model,
        score: adjustedScore,
        reasons: result.reasons,
      };
    });

    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);

    // Guard: no models available
    if (scored.length === 0) {
      return {
        model: "none",
        provider: "none",
        tier: "economy" as const,
        reason: "No models available in the registry",
        estimatedCost: 0,
        confidence: "low" as const,
        dataPoints: 0,
        alternatives: [],
        guardrailsToInject: [...guardrailTemplates, ...qualityGuardrails],
        budgetWarning: undefined,
      };
    }

    const totalTokens = estimateTokensForTask(task);

    // If budget is provided, check if top pick fits budget
    let budgetWarning: string | undefined;
    let topPick = scored[0];

    if (budgetState) {
      const topCost = estimateCost(topPick.model, totalTokens);
      const budgetCheck = checkBudget(budgetState, topCost);

      if (!budgetCheck.ok) {
        // Find the cheapest model that still has reasonable capability
        const affordable = scored.find((s) => {
          const cost = estimateCost(s.model, totalTokens);
          return checkBudget(budgetState, cost).ok;
        });
        if (affordable) {
          budgetWarning = budgetCheck.warning;
          topPick = affordable;
        } else {
          // No model fits — use cheapest and warn
          const cheapest = [...scored].sort(
            (a, b) =>
              estimateCost(a.model, totalTokens) -
              estimateCost(b.model, totalTokens),
          )[0];
          topPick = cheapest;
          budgetWarning = budgetCheck.warning;
        }
      } else if (budgetCheck.warning) {
        budgetWarning = budgetCheck.warning;
      }
    }

    const topCost = estimateCost(topPick.model, totalTokens);

    // Build alternatives (up to 2, excluding the top pick)
    const alternatives = scored
      .filter((s) => s.model.id !== topPick.model.id)
      .slice(0, 2)
      .map((s) => ({
        model: s.model.id,
        reason: s.reasons[0] ?? "Alternative option",
        estimatedCost: estimateCost(s.model, totalTokens),
      }));

    // Determine confidence based on calibration data
    const dataPoints = calibration
      ? calibration.entries.filter(
          (e) =>
            e.model === topPick.model.id &&
            (!task.taskType || e.taskType === task.taskType),
        ).length
      : 0;

    let confidence: "high" | "medium" | "low";
    if (dataPoints >= 10) confidence = "high";
    else if (dataPoints >= 3) confidence = "medium";
    else confidence = "low";

    // Collect guardrails to inject
    const guardrailsToInject = [...guardrailTemplates, ...qualityGuardrails];

    // Build reason
    const mainReason = buildReason(topPick.model, task, topCost, topPick.reasons);

    // Check for model deprecation
    let deprecationWarning: string | undefined;
    if (topPick.model.deprecated) {
      deprecationWarning = `Model ${topPick.model.name} is deprecated. Consider ${topPick.model.successor ?? "a newer alternative"} instead.`;
    }

    return {
      model: topPick.model.id,
      provider: topPick.model.provider,
      tier: topPick.model.tier,
      reason: mainReason,
      estimatedCost: topCost,
      confidence,
      dataPoints,
      alternatives,
      guardrailsToInject,
      budgetWarning,
      deprecationWarning,
    };
  }

  function recommendBatch(
    tasks: TaskContext[],
    budgetState?: BudgetState,
  ): Recommendation[] {
    // For batch: recommend each task individually but track cumulative cost
    let cumulativeCost = 0;
    const trackingBudget = budgetState
      ? { ...budgetState }
      : undefined;

    return tasks.map((task) => {
      const rec = recommend(task, trackingBudget);
      cumulativeCost += rec.estimatedCost;
      if (trackingBudget) {
        trackingBudget.currentSpend += rec.estimatedCost;
      }
      return rec;
    });
  }

  function getDefaultModel(
    tier: "frontier" | "mid" | "economy",
  ): ModelInfo | undefined {
    return models.find((m) => m.tier === tier);
  }

  return { recommend, recommendBatch, getDefaultModel };
}

// ── Helpers ──────────────────────────────────────────────────

function buildReason(
  model: ModelInfo,
  task: TaskContext,
  cost: number,
  reasons: string[],
): string {
  const taskDesc = task.taskType
    ? `${task.taskType} (${task.complexity})`
    : `${task.complexity} complexity task`;

  const costStr = cost < 0.01
    ? `<$0.01`
    : `$${cost.toFixed(2)}`;

  return `${model.name} recommended for ${taskDesc} at ~${costStr}. ${reasons[0] ?? ""}`.trim();
}
